import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { Menu, X } from "lucide-react";
import akLogo from "@/assets/ak-logo-badge.png";
import { useEffect, useState } from "react";
import { WaitlistDialog } from "@/components/WaitlistDialog";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useI18n } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const HOME_SECTIONS: { hash: string; key: string }[] = [
  { hash: "pricing", key: "nav.section.pricing" },
];

const AGENCY_SECTIONS: { hash: string; key: string }[] = [
  { hash: "agency-pricing", key: "nav.section.pricing" },
];

export function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<string>("hero");

  const isAgencies = location.pathname === "/agencies";
  const isHome = location.pathname === "/";
  const basePath = isAgencies ? "/agencies" : "/";
  const SECTIONS = isAgencies ? AGENCY_SECTIONS : HOME_SECTIONS;

  useEffect(() => {
    if (!isHome && !isAgencies) return;
    if (typeof window === "undefined") return;

    const els = SECTIONS
      .map((s) => document.getElementById(s.hash))
      .filter((el): el is HTMLElement => !!el);
    if (els.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-40% 0px -50% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [location.pathname]);

  const handleHashNav = (e: React.MouseEvent<HTMLAnchorElement>, hash: string) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== basePath) {
      navigate({ to: basePath, hash });
    } else {
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
        history.replaceState(null, "", `#${hash}`);
        setActive(hash);
      }
    }
  };

  const isActiveSection = (hash: string) => (isHome || isAgencies) && active === hash;

  return (
    <header
      className="fixed top-0 left-0 right-0 w-full z-50"
      style={{ background: "rgba(10, 24, 69, 0.85)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}
    >
      <nav className="max-w-7xl mx-auto px-6 h-18 py-4 flex items-center justify-between">

        <Link to="/" className="flex items-center gap-2" onClick={() => setOpen(false)}>
          <img src={akLogo} alt="AdKavi logo" className="w-10 h-10 rounded-full object-cover" />
          <span
            className="text-2xl tracking-tight"
            style={{
              fontWeight: 700,
              background: "linear-gradient(135deg, #f97316, #dc2626)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            AdKavi
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2 py-1.5 text-sm font-medium text-muted-foreground backdrop-blur-sm">
          {isAgencies && (
            <Link to="/" className="px-3 py-1.5 rounded-full hover:text-foreground hover:bg-white/10 transition">{t("nav.home")}</Link>
          )}
          {SECTIONS.map((s) => (
            <a
              key={s.hash}
              href={`${basePath}#${s.hash}`}
              onClick={(e) => handleHashNav(e, s.hash)}
              className={cn(
                "px-3 py-1.5 rounded-full transition hover:text-foreground hover:bg-white/10",
                isActiveSection(s.hash) && "text-foreground bg-white/10",
              )}
            >
              {t(s.key)}
            </a>
          ))}
          <Link
            to="/agencies"
            className={cn(
              "px-3 py-1.5 rounded-full transition hover:text-foreground hover:bg-white/10",
              isAgencies && "text-foreground bg-white/10",
            )}
          >
            {t("nav.agencies")}
          </Link>
          <Link
            to="/auth"
            className="px-3 py-1.5 rounded-full border border-white/20 text-white/80 hover:text-white hover:border-white/40 transition ml-1"
          >
            {t("nav.login")}
          </Link>
        </div>

        <div className="flex items-center gap-2">
          <div className="hidden md:block"><LanguageSwitcher variant="compact" /></div>
          <WaitlistDialog>
            <button
              style={{ backgroundImage: "linear-gradient(135deg, #f97316, #dc2626)" }}
              className="hidden sm:inline-flex items-center justify-center rounded-full px-5 py-2.5 text-sm font-semibold text-white hover:brightness-110 transition glow-orange"
            >
              {t("nav.joinWaitlist")}
            </button>
          </WaitlistDialog>
          <button
            type="button"
            aria-label={open ? t("nav.menu.close") : t("nav.menu.open")}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg border border-border text-foreground"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="md:hidden border-t border-border/50 bg-[var(--nav-background)]/95 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
            {isAgencies && (
              <Link
                to="/"
                onClick={() => setOpen(false)}
                className="px-3 py-3 rounded-lg text-base font-medium text-muted-foreground hover:text-foreground hover:bg-foreground/5 transition"
              >
                {t("nav.home")}
              </Link>
            )}
            {SECTIONS.map((s) => (
              <a
                key={s.hash}
                href={`${basePath}#${s.hash}`}
                onClick={(e) => handleHashNav(e, s.hash)}
                className={cn(
                  "px-3 py-3 rounded-lg text-base font-medium transition",
                  isActiveSection(s.hash)
                    ? "bg-primary/10 text-foreground font-semibold border-l-2 border-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-foreground/5",
                )}
              >
                {t(s.key)}
              </a>
            ))}
            <Link
              to="/agencies"
              onClick={() => setOpen(false)}
              className={cn(
                "px-3 py-3 rounded-lg text-base font-medium transition",
                isAgencies
                  ? "bg-primary/10 text-foreground font-semibold border-l-2 border-primary"
                  : "text-muted-foreground hover:text-foreground hover:bg-foreground/5",
              )}
            >
              {t("nav.agencies")}
            </Link>
            <Link
              to="/auth"
              onClick={() => setOpen(false)}
              className="px-3 py-3 rounded-lg text-base font-medium text-muted-foreground hover:text-foreground hover:bg-foreground/5 transition"
            >
              {t("nav.login")}
            </Link>
            <WaitlistDialog>
              <button
                onClick={() => setOpen(false)}
                style={{ backgroundImage: "linear-gradient(135deg, #f97316, #dc2626)" }}
                className="mt-2 w-full inline-flex items-center justify-center rounded-full px-5 py-3 text-base font-semibold text-white hover:brightness-110 transition glow-orange"
              >
                {t("nav.joinWaitlist")}
              </button>
            </WaitlistDialog>
          </div>
        </div>
      )}
    </header>
  );
}
